import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import './theme/theme.css';

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LoadingOverlay, MantineProvider } from '@mantine/core';
import { theme, themedResolver } from './theme/theme';
import { BaseLayout } from './layouts/BaseLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, Suspense } from 'react';
import SEOHeader from './components/seo/header';
import { CookieConsentManager } from './components/seo/concent';
import AdminShippingPage from './pages/admin/AdminShippingPage';
import AdminStoresPage from './pages/admin/AdminStoresPage';

// Lazy load all pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/register/RegisterPage'));
const ForgotPasswordPage = lazy(
  () => import('./pages/auth/ForgotPasswordPage')
);
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const ListingPurchasePage = lazy(
  () => import('./pages/store/ListingPurchasePage')
);
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const LogoutPage = lazy(() => import('./pages/auth/LogoutPage'));
const RafflesPage = lazy(() => import('./pages/raffles/RafflesPage'));
const ListingsPage = lazy(() => import('./pages/listings/ListingsPage'));
const AccountPage = lazy(() => import('./pages/account/AccountPage'));
const AccountHistoryPage = lazy(() => import('./pages/AccountHistory'));
const StorePage = lazy(() => import('./pages/store/StorePage'));
const StoresPage = lazy(() => import('./pages/stores/StoresPage'));
const StoreBackOfficePage = lazy(
  () => import('./pages/store/StoreBackOfficePage')
);
const AdminListingDetailPage = lazy(
  () => import('./pages/store/AdminListingDetailPage')
);
const NewListingPage = lazy(() => import('./pages/store/NewListingPage'));
const MediaLibraryPage = lazy(() => import('./pages/store/MediaLibraryPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const TermsPage = lazy(() => import('./pages/standard/TermsPage'));
const StoreTermsPage = lazy(() => import('./pages/standard/StoreTermsPage'));
const AboutPage = lazy(() => import('./pages/standard/about'));
const ContactPage = lazy(() => import('./pages/standard/contact'));
const PrivacyPage = lazy(() => import('./pages/standard/privacy'));
const RandomPage = lazy(() => import('./pages/standard/random'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider
        theme={theme}
        cssVariablesResolver={themedResolver}
        defaultColorScheme="light"
      >
        <Notifications position="bottom-right" zIndex={2000} />
        <Suspense fallback={<LoadingOverlay />}>
          <Router>
            <Routes>
              {/* Auth Routes */}
              <Route element={<BaseLayout />}>
                <Route
                  path="/auth/login"
                  element={
                    <>
                      <SEOHeader pageKey="login" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <LoginPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/auth/register"
                  element={
                    <>
                      <SEOHeader pageKey="register" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <RegisterPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/auth/forgot-password"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <ForgotPasswordPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/auth/reset-password"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <ResetPasswordPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/auth/verify-email"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <VerifyEmailPage />
                    </Suspense>
                  }
                />
              </Route>

              {/* Protected Routes */}
              <Route element={<BaseLayout />}>
                <Route
                  path="/"
                  element={
                    <>
                      <SEOHeader pageKey="home" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <RafflesPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/draw"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <RandomPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/hotlistings"
                  element={
                    <>
                      <SEOHeader pageKey="home" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <RafflesPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute requiredStep={5}>
                      <Suspense fallback={<LoadingOverlay />}>
                        <AccountPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account/history"
                  element={
                    <ProtectedRoute requiredStep={5}>
                      <Suspense fallback={<LoadingOverlay />}>
                        <AccountHistoryPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/auth/logout"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <LogoutPage />
                    </Suspense>
                  }
                />
                {/* non-protected routes */}
                <Route
                  path="/listings"
                  element={
                    <>
                      <SEOHeader pageKey="home" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <ListingsPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/listing"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <RafflesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/listing/:listingId"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <ListingPurchasePage />
                    </Suspense>
                  }
                />

                <Route
                  path="/stores"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <StoresPage />
                    </Suspense>
                  }
                />

                {/* Store Admin Routes */}
                <Route
                  path="/store/:storeId/back-office"
                  element={
                    <ProtectedRoute requiredStep={5}>
                      <Suspense fallback={<LoadingOverlay />}>
                        <StoreBackOfficePage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/store/:storeId/back-office/listings/new"
                  element={
                    <ProtectedRoute requiredStep={5}>
                      <Suspense fallback={<LoadingOverlay />}>
                        <NewListingPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/store/:storeId/back-office/listings/:listingId"
                  element={
                    <ProtectedRoute requiredStep={5}>
                      <Suspense fallback={<LoadingOverlay />}>
                        <AdminListingDetailPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/store/:storeId/back-office/media"
                  element={
                    <ProtectedRoute requiredStep={5}>
                      <Suspense fallback={<LoadingOverlay />}>
                        <MediaLibraryPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route element={<BaseLayout />}>
                <Route
                  path="/store/:storeName"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <StorePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/terms"
                  element={
                    <>
                      <SEOHeader pageKey="terms" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <TermsPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/store-terms"
                  element={
                    <>
                      <SEOHeader pageKey="sellerTerms" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <StoreTermsPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/about-us"
                  element={
                    <>
                      <SEOHeader pageKey="about" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <AboutPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/contact"
                  element={
                    <>
                      <SEOHeader pageKey="contact" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <ContactPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/privacy"
                  element={
                    <>
                      <SEOHeader pageKey="privacy" />
                      <Suspense fallback={<LoadingOverlay />}>
                        <PrivacyPage />
                      </Suspense>
                    </>
                  }
                />

                <Route
                  path="/siteadmin/shipping"
                  element={
                    <>
                      <Suspense fallback={<LoadingOverlay />}>
                        <AdminShippingPage />
                      </Suspense>
                    </>
                  }
                />
                <Route
                  path="/siteadmin/stores"
                  element={
                    <>
                      <Suspense fallback={<LoadingOverlay />}>
                        <AdminStoresPage />
                      </Suspense>
                    </>
                  }
                />
              </Route>

              <Route element={<BaseLayout />}>
                <Route
                  path="*"
                  element={
                    <Suspense fallback={<LoadingOverlay />}>
                      <NotFoundPage />
                    </Suspense>
                  }
                />
              </Route>
            </Routes>
          </Router>
        </Suspense>
        <CookieConsentManager />
      </MantineProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;
